import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import Loading from 'views/Widgets/Loading';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';
import { MetadataItem } from '../AgentsAdmin/useAgentMetadata';
import ResourceMentionEditor from './ResourceMentionEditor';

interface CustomSkill {
  id: number;
  name: string;
  description: string;
  instructions: string;
  created_at: string;
  updated_at: string;
}

interface Props {
  isOpen: boolean;
  toggle: () => void;
  skillId: number;
  onSuccess: (updatedSkill: unknown) => void;
  resources: MetadataItem[];
}

const EditCustomSkillModal: FC<Props> = ({
  isOpen,
  toggle,
  skillId,
  onSuccess,
  resources,
}) => {
  const { formatMessage } = useIntl();
  const [skill, setSkill] = useState<CustomSkill | null>(null);

  const { data, status } = useConfirmApi<CustomSkill>({
    url: `ai-custom-skills/${skillId}`,
    method: 'GET',
    disabled: !isOpen,
  });

  useEffect(() => {
    if (data) {
      setSkill(data);
    }
  }, [data]);

  const handleCallback = useCallback(
    (responseData: unknown, error: unknown) => {
      if (responseData) {
        toast.success(
          formatMessage({
            id: 'app.views.ai.agents.skills_admin.edit_modal.success',
            defaultMessage: 'Custom skill saved successfully',
          })
        );
        onSuccess(responseData);
        toggle();
      } else if (error) {
        toast.error(
          formatMessage({
            id: 'app.views.ai.agents.skills_admin.edit_modal.error',
            defaultMessage: 'Failed to save custom skill',
          })
        );
      }
    },
    [formatMessage, onSuccess, toggle]
  );

  const inputs = useMemo(
    () => [
      {
        required: true,
        name: 'name',
        label: formatMessage({
          id: 'app.views.ai.agents.skills_admin.edit_modal.fields.name.label',
          defaultMessage: 'Skill Name',
        }),
        placeholder: formatMessage({
          id: 'app.views.ai.agents.skills_admin.edit_modal.fields.name.placeholder',
          defaultMessage: 'Enter skill name',
        }),
        type: INPUT_TYPES.TEXT,
        ignorePasswordManagers: true,
        autoFocus: true,
      },
      {
        required: false,
        name: 'description',
        label: formatMessage({
          id: 'app.views.ai.agents.skills_admin.edit_modal.fields.description.label',
          defaultMessage: 'Description',
        }),
        helperText: formatMessage({
          id: 'app.views.ai.agents.skills_admin.edit_modal.fields.description.helper_text',
          defaultMessage: 'A brief summary of what this skill does.',
        }),
        placeholder: formatMessage({
          id: 'app.views.ai.agents.skills_admin.edit_modal.fields.description.placeholder',
          defaultMessage: 'Enter a description (optional)',
        }),
        type: INPUT_TYPES.TEXTAREA,
      },
      {
        required: true,
        name: 'instructions',
        label: formatMessage({
          id: 'app.views.ai.agents.skills_admin.edit_modal.fields.instructions.label',
          defaultMessage: 'Instructions',
        }),
        helperText: formatMessage({
          id: 'app.views.ai.agents.skills_admin.edit_modal.fields.instructions.helper_text',
          defaultMessage:
            'Write instructions for the AI agent. Use @ to reference available resources.',
        }),
        type: INPUT_TYPES.CUSTOM_COMPONENT,
        component: (props) => (
          <ResourceMentionEditor
            value={props.value || ''}
            onChange={props.onChange}
            resources={resources}
          />
        ),
      },
    ],
    [formatMessage, resources]
  );

  if (status === 'LOADING' || !skill) {
    return (
      <Loading
        layout="overlay"
        message={formatMessage({
          id: 'app.views.ai.agents.skills_admin.edit_modal.loading',
          defaultMessage: 'Loading skill...',
        })}
      />
    );
  }

  return (
    <ModalEditor
      isOpen={isOpen}
      toggle={toggle}
      object={skill}
      inputs={inputs}
      method="PATCH"
      url="ai-custom-skills"
      submitText={formatMessage({
        id: 'app.views.ai.agents.skills_admin.edit_modal.submit_text',
        defaultMessage: 'Save Changes',
      })}
      title={formatMessage({
        id: 'app.views.ai.agents.skills_admin.edit_modal.title',
        defaultMessage: 'Edit Custom Skill',
      })}
      callback={handleCallback}
      keepOpenOnSuccess={true}
    />
  );
};

export default EditCustomSkillModal;
